import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { CollectForm_Base } from '~/common/typescript/models/CollectForm';
import { parseFormContent, findTitleField, ParsedFormField } from '../utils/CollectFormParser';

export interface CollectColumnConfigItem {
  key: string;
  label: string;
  description: string;
  type: string;
  visible: boolean;
  pinned?: boolean;
  locked?: boolean;
  isSystem?: boolean;
}

export interface PersistedColumnPref {
  key: string;
  visible: boolean;
  pinned?: boolean;
}

export interface ColumnSettingsPanelSize {
  width: number;
  height: number;
}

export const selectedFormUUIDAtom = atom<string | null>(null);

export const availableFormsAtom = atom<CollectForm_Base[]>([]);

// Cache for individually loaded forms
export const singleFormsCacheAtom = atom<Record<string, CollectForm_Base>>({});

// Combined dictionary of all collect forms indexed by UUID
export const allCollectFormsMapAtom = atom<Record<string, CollectForm_Base>>((get) => {
  const forms = get(availableFormsAtom);
  const singleCache = get(singleFormsCacheAtom);
  const map: Record<string, CollectForm_Base> = { ...singleCache };
  forms.forEach((form) => {
    if (form.uuid) {
      map[form.uuid] = form;
    }
  });
  return map;
});

// Map of parsed fields indexed by form UUID and field key
export const collectFormFieldsMapAtom = atom<Record<string, Record<string, ParsedFormField>>>((get) => {
  const formsMap = get(allCollectFormsMapAtom);
  const result: Record<string, Record<string, ParsedFormField>> = {};

  Object.keys(formsMap).forEach((uuid) => {
    const form = formsMap[uuid];
    if (form) {
      const parsed = parseFormContent(form);
      if (parsed && parsed.fields) {
        const fieldsByKey: Record<string, ParsedFormField> = {};
        parsed.fields.forEach((field) => {
          if (field.key) {
            fieldsByKey[field.key] = field;
          }
        });
        result[uuid] = fieldsByKey;
      }
    }
  });

  return result;
});

// Pagination for collect forms
export interface PaginationInfo {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

export const collectFormsPaginationAtom = atom<PaginationInfo>({
  page: 1,
  pageSize: 10,
  pageCount: 0,
  total: 0,
});

// Derived atom: get selected form data from availableForms based on UUID
export const selectedFormDataAtom = atom((get) => {
  const uuid = get(selectedFormUUIDAtom);
  const formsMap = get(allCollectFormsMapAtom);
  if (!uuid) return null;
  return formsMap[uuid] || null;
});

export const isMaximizedAtom = atom<boolean>(false);

/**
 * Helper: Build default column configs from a CollectForm schema
 */
export const buildDefaultColumnConfigsForForm = (
  formData: CollectForm_Base | null,
): CollectColumnConfigItem[] => {
  const items: CollectColumnConfigItem[] = [
    {
      key: 'uuid',
      label: 'UUID',
      description: 'Mã định danh duy nhất của bản ghi thu thập',
      type: 'uuid',
      visible: true,
      pinned: true,
      isSystem: true,
    },
    {
      key: 'preview',
      label: 'Hình học',
      description: 'Hình học không gian (Điểm / Đường / Vùng)',
      type: 'geometry',
      visible: true,
      pinned: false,
      isSystem: true,
    },
  ];

  const titleField = findTitleField(formData);
  const parsedContent = parseFormContent(formData);
  if (parsedContent && parsedContent.fields.length > 0) {
    parsedContent.fields.forEach((field) => {
      const keyLower = (field.key || '').toLowerCase();
      const isTitleColumn =
        Boolean(titleField && field.key === titleField.key) ||
        keyLower === 'name' ||
        keyLower === 'ten';

      items.push({
        key: field.key,
        label: field.label || field.key,
        description:
          field.description ||
          `Trường biểu mẫu: ${field.key}${field.required ? ' • Bắt buộc' : ''}`,
        type: field.type || 'text',
        visible: true,
        pinned: false,
        locked: isTitleColumn,
        isSystem: false,
      });
    });
  }

  items.push(
    {
      key: 'sync_at',
      label: 'Ngày đồng bộ',
      description: 'Thời điểm đồng bộ dữ liệu từ thiết bị lên máy chủ',
      type: 'datetime',
      visible: true,
      pinned: false,
      isSystem: true,
    },
    {
      key: 'user',
      label: 'Người tạo',
      description: 'Nhân viên thực hiện thu thập ngoài thực địa',
      type: 'user',
      visible: true,
      pinned: false,
      isSystem: true,
    },
  );

  return items;
};

/**
 * Persisted Jotai atom storing per-form column preferences in localStorage
 * Key: `geocollect_columns_config_map_v1`
 */
export const collectColumnsPersistedMapAtom = atomWithStorage<
  Record<string, PersistedColumnPref[]>
>(
  'geocollect_columns_config_map_v1',
  {},
  undefined,
  { getOnInit: true },
);

/**
 * Persisted Jotai atom storing ColumnSettingsPopover panel dimensions (width & height) in localStorage
 * Key: `geocollect_column_settings_panel_size_v1`
 */
export const columnSettingsPanelSizeAtom = atomWithStorage<ColumnSettingsPanelSize>(
  'geocollect_column_settings_panel_size_v1',
  { width: 440, height: 480 },
  undefined,
  { getOnInit: true },
);

/**
 * Read/Write derived atom for the currently selected form's column configs.
 * Automatically merges form schema defaults with persisted localStorage preferences.
 */
export const activeFormColumnConfigsAtom = atom(
  (get): CollectColumnConfigItem[] => {
    const formUUID = get(selectedFormUUIDAtom);
    const formData = get(selectedFormDataAtom);
    const persistedMap = get(collectColumnsPersistedMapAtom);
    const defaults = buildDefaultColumnConfigsForForm(formData);

    if (!formUUID) {
      return defaults;
    }

    const saved = persistedMap[formUUID];
    if (!Array.isArray(saved) || saved.length === 0) {
      return defaults;
    }

    const defaultMap = new Map(defaults.map((item) => [item.key, item]));
    const merged: CollectColumnConfigItem[] = [];

    saved.forEach((s) => {
      const match = defaultMap.get(s.key);
      if (match) {
        merged.push({
          ...match,
          visible: match.locked
            ? true
            : typeof s.visible === 'boolean'
            ? s.visible
            : true,
          pinned: typeof s.pinned === 'boolean' ? s.pinned : match.pinned,
          locked: match.locked,
        });
        defaultMap.delete(s.key);
      }
    });

    // Append any newly added form fields not present in saved preferences
    defaultMap.forEach((remaining) => {
      merged.push(remaining);
    });

    return merged;
  },
  (get, set, nextColumns: CollectColumnConfigItem[]) => {
    const formUUID = get(selectedFormUUIDAtom);
    if (!formUUID) return;

    const sanitized: PersistedColumnPref[] = nextColumns.map((c) => ({
      key: c.key,
      visible: c.locked ? true : Boolean(c.visible),
      pinned: Boolean(c.pinned),
    }));

    const prevMap = get(collectColumnsPersistedMapAtom);
    set(collectColumnsPersistedMapAtom, {
      ...prevMap,
      [formUUID]: sanitized,
    });
  },
);

/**
 * Write-only atom to reset the active form's column configs in persisted storage
 */
export const resetActiveFormColumnConfigsAtom = atom(null, (get, set) => {
  const formUUID = get(selectedFormUUIDAtom);
  if (!formUUID) return;

  const prevMap = get(collectColumnsPersistedMapAtom);
  const nextMap = { ...prevMap };
  delete nextMap[formUUID];
  set(collectColumnsPersistedMapAtom, nextMap);
});
